import React, { useEffect, useState } from "react";
import { IBusDetail, ISeatDetail, ITripDetail } from "../../../../models/trip";
import SeatPill from "./SeatPill";
import { getSeatApi } from "../../../../apis/seat.api";
import { Divider, Spin, Tag } from "antd";
import IconDriver from "../../../../assets/icon/icon-driver.png";
import IconDoor from "../../../../assets/icon/icon-door.png";
import { UserOutlined } from "@ant-design/icons";

interface LayoutSeatItemProps {
  bus: IBusDetail;
  // if true, bypass the module cache and force a network refresh
  forceRefresh?: boolean;
  onSetTrip: React.Dispatch<React.SetStateAction<ITripDetail | null>>;
  handleSeatClick: (seat: ISeatDetail) => void;
  selectMode: string | null;
  targetCustomer: string | null;
  selectedSeats: ISeatDetail[];
  setBusIdChanged: React.Dispatch<React.SetStateAction<string[]>>;
  busIdChanged: string[];
}

const LayoutSeatItem: React.FC<LayoutSeatItemProps> = (
  props: LayoutSeatItemProps
) => {
  const {
    bus,
    forceRefresh,
    onSetTrip,
    handleSeatClick,
    selectMode,
    selectedSeats,
    targetCustomer,
    busIdChanged,
    setBusIdChanged,
  } = props;
  const { busType, _id, tripId, seats } = bus;

  const [loading, setLoading] = useState(false);
  const handleGetSeatsForBus = async (force = false) => {
    if (!_id || !tripId) return;

    // Nếu không 'force' và đã có seats -> không fetch
    if (!force && seats?.length) return;

    setLoading(true);
    try {
      const res = await getSeatApi(_id, tripId);
      if (!res?.success) throw new Error(res?.message || "Lỗi khi lấy ghế");
      const data = (res.data as ISeatDetail[]) ?? [];
      onSetTrip((prev) => updateBusInTrip(prev, _id, { seats: data }));
      // Sau khi load xong, xóa id khỏi danh sách cần refresh
      setBusIdChanged((prev) => prev.filter((id) => id !== _id));
    } catch (err: any) {
      console.error("Error fetching seats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!_id || !tripId) return;

    handleGetSeatsForBus();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, tripId, forceRefresh]);
  useEffect(() => {
    if (busIdChanged?.includes(_id)) {
      handleGetSeatsForBus(true); // bắt buộc fetch dù đã có seats
    }
  }, [busIdChanged]);
  // Xác định số hàng chính
  const mainRows = busType === 45 ? 10 : 7;
  // Danh sách hàng (1 → mainRows)
  const rows = Array.from({ length: mainRows }, (_, i) => i + 1);

  // Hàm render 1 hàng ghế (A–D)
  const renderRow = (row: number) => {
    const rowSeats = seats.filter((s) => s.row === row);

    const leftSeats = rowSeats.filter(
      (s) => s.column === "A" || s.column === "B"
    );
    const rightSeats = rowSeats.filter(
      (s) => s.column === "C" || s.column === "D"
    );

    return (
      <div key={row} className="flex gap-3 justify-between w-full">
        {/* Bên trái */}
        <div className="flex gap-1.5 w-full justify-between">
          {leftSeats.map((seat) => (
            <SeatPill
              disabled={
                (selectMode === "add" && seat.status !== "available") ||
                (selectMode === "edit" && seat.customerId !== targetCustomer)
              }
              onClick={(value) => handleSeatClick(value)}
              isSelected={selectedSeats.some((s) => s._id === seat._id)}
              key={seat?._id}
              seat={seat}
              className="w-[50%] mb-0.5"
            />
          ))}
        </div>

        {/* Bên phải */}
        <div className="flex gap-1.5  w-full ">
          {rightSeats.map((seat) => (
            <SeatPill
              disabled={
                (selectMode === "add" && seat.status !== "available") ||
                (selectMode === "edit" && seat.customerId !== targetCustomer)
              }
              onClick={(value) => handleSeatClick(value)}
              isSelected={selectedSeats.some((s) => s._id === seat._id)}
              key={seat?._id}
              seat={seat}
              className="w-[50%] mb-0.5"
            />
          ))}
        </div>
      </div>
    );
  };
  const renderLastRow = () => {
    const lastSeats = seats.filter((s) => s.row === 11);
    if (lastSeats.length === 0) return null;

    return (
      <div key="row-11" className="flex justify-between gap-1.5 mt-1 w-full">
        {lastSeats.map((seat) => (
          <SeatPill
            disabled={
              (selectMode === "add" && seat.status !== "available") ||
              (selectMode === "edit" && seat.customerId !== targetCustomer)
            }
            onClick={(value) => handleSeatClick(value)}
            isSelected={selectedSeats.some((s) => s._id === seat._id)}
            key={seat?._id}
            seat={seat}
            className="w-full"
          />
        ))}
      </div>
    );
  };
  const renderSpareRow = () => {
    const maxRow = getMaxNormalRow(bus.busType);

    const spareRows = Array.from(
      new Set(seats.filter((s) => s.row > maxRow).map((s) => s.row))
    ).sort((a, b) => a - b);

    if (spareRows.length === 0) return null;

    return spareRows.map((row) => {
      const rowSeats = seats.filter((s) => s.row === row);

      const leftSeats = rowSeats.filter(
        (s) => s.column === "A" || s.column === "B"
      );
      const rightSeats = rowSeats.filter(
        (s) => s.column === "C" || s.column === "D"
      );

      return (
        <div
          key={`spare-row-${row}`}
          className="flex justify-between gap-3 mt-1 w-full"
        >
          {/* Trái (A, B) */}
          <div className="flex gap-1.5 w-full justify-between">
            {leftSeats.map((seat) => (
              <SeatPill
                key={seat._id}
                seat={seat}
                disabled={
                  (selectMode === "add" && seat.status !== "available") ||
                  (selectMode === "edit" && seat.customerId !== targetCustomer)
                }
                isSelected={selectedSeats.some((s) => s._id === seat._id)}
                onClick={(value) => handleSeatClick(value)}
                className="w-[50%]"
              />
            ))}
          </div>

          {/* Phải (C, D) */}
          <div className="flex gap-1.5 w-full">
            {rightSeats.map((seat) => (
              <SeatPill
                key={seat._id}
                seat={seat}
                disabled={
                  (selectMode === "add" && seat.status !== "available") ||
                  (selectMode === "edit" && seat.customerId !== targetCustomer)
                }
                isSelected={selectedSeats.some((s) => s._id === seat._id)}
                onClick={(value) => handleSeatClick(value)}
                className="w-[50%]"
              />
            ))}
          </div>
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center py-10">
        <Spin size="large" />
      </div>
    );
  }

  // không loading
  if (!seats || seats.length === 0) {
    return <></>; // seats trống → không hiển thị
  }
  return (
    <div className="flex flex-col items-center gap-1 w-full mt-1">
      {loading ? (
        <div className="w-full ">
          <Spin
            children={
              <div className="w-full">
                <div className="flex justify-between items-center mb-2">
                  <div className="rounded-md border p-3 flex  items-center gap-3">
                    <img
                      src={IconDriver}
                      alt="Tài xế"
                      className="w-8 h-8 object-contain"
                    />
                    <p className="font-medium text-gray-600">Tài xế</p>
                  </div>
                  <div className="rounded-md border p-3 flex  items-center gap-3">
                    <p className="font-medium text-gray-600">Cửa</p>
                    <img
                      src={IconDoor}
                      alt="Cửa"
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                </div>
                {rows.map(renderRow)}
                {busType === 45 && renderLastRow()}
                {summarizeBookedPassengers(seats).length > 0 && (
                  <Divider orientation="left">
                    Danh sách hành khách {bus?.busNumber}
                  </Divider>
                )}

                <div className=" space-y-3">
                  {summarizeBookedPassengers(seats).map((passenger, i) => (
                    <div
                      key={passenger.customerId || i}
                      className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50 to-white p-3 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                        <UserOutlined className="text-blue-600 text-lg" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-800">
                            {passenger.shortName}
                          </p>
                          <Tag color="blue">
                            <p className="text-base font-medium">
                              {passenger.numberOfSeat} ghế
                            </p>
                          </Tag>
                        </div>

                        <p className="mt-1 text-sm text-gray-600">
                          Ghế:{" "}
                          <span className="font-medium text-gray-800">
                            {passenger.seats.join(", ")}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
          />
        </div>
      ) : (
        <div className="w-full">
          <div className="flex justify-between items-center mb-2">
            <div className="rounded-xl border p-3 flex  items-center gap-3">
              <img
                src={IconDriver}
                alt="Tài xế"
                className="w-8 h-8 object-contain"
              />
              <p className="font-medium text-gray-600">Tài xế</p>
            </div>
            <div className="rounded-xl border p-3 flex  items-center gap-3">
              <p className="font-medium text-gray-600">Cửa</p>
              <img
                src={IconDoor}
                alt="Cửa"
                className="w-8 h-8 object-contain"
              />
            </div>
          </div>
          {rows.map(renderRow)}
          {busType === 45 && renderLastRow()}

          {(() => {
            const maxRow = getMaxNormalRow(bus?.busType);

            const hasSpareSeats = seats?.some((s) => s.row > maxRow);

            if (!hasSpareSeats) return null;

            return (
              <>
                <Divider className="">Ghế dư</Divider>
                {renderSpareRow()}
              </>
            );
          })()}
          {summarizeBookedPassengers(seats).length > 0 && (
            <Divider orientation="left">
              {" "}
              Danh sách hành khách xe {bus?.busNumber}
            </Divider>
          )}

          <div className=" space-y-3">
            {summarizeBookedPassengers(seats).map((passenger, i) => (
              <div
                key={passenger?.customerId || i}
                className="flex items-center gap-3 rounded-xl p-2 divide-dotted border-2 border-red-200 bg-red-50 shadow-sm "
              >
                <a
                  href={`tel:${passenger?.majorPhone}`}
                  className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-medium h-10 w-10 rounded-full shadow transition-colors"
                >
                  <i className="fa-solid fa-phone"></i>
                </a>

                <div className="flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <div>
                      <p className="font-semibold text-gray-800">
                        {passenger.shortName}
                      </p>
                      <p className="mt-1 text-sm text-gray-600">
                        Ghế:{" "}
                        <span className="font-medium text-gray-800">
                          {passenger.seats.join(", ")}
                        </span>
                      </p>
                    </div>
                    <div>
                    
                      <div className="flex justify-end whitespace-nowrap rounded-full bg-orange-500 px-3 py-1 text-white font-semibold shadow-md">
                        <p className=" whitespace-nowrap"> {passenger.numberOfSeat} ghế</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const getMaxNormalRow = (busType: number) => {
  switch (busType) {
    case 45:
      return 11; // xe 45 chỗ, max row chuẩn là 11
    case 29:
      return 7; // xe 28/29 chỗ
    case 16:
      return 5; // xe 16 chỗ
    default:
      return 0;
  }
};

export default LayoutSeatItem;
interface PassengerSummary {
  customerId: string;
  majorPhone?: string;
  shortName: string;
  numberOfSeat: number;
  seats: string[];
}

export const summarizeBookedPassengers = (
  data: ISeatDetail[]
): PassengerSummary[] => {
  const map = new Map<string, PassengerSummary>();

  data.forEach((seat) => {
    if (seat.status === "booked" && seat.customerId) {
      const shortName = seat.customerDetail?.shortName || "Unknown";
      const existing = map.get(seat.customerId);

      if (!existing) {
        map.set(seat.customerId, {
          customerId: seat.customerId,
          shortName,
          majorPhone: seat.customerDetail?.majorPhone,
          numberOfSeat: 1,
          seats: [seat.seatCode],
        });
      } else {
        existing.numberOfSeat += 1;
        existing.seats.push(seat.seatCode);
      }
    }
  });

  return Array.from(map.values());
};
export function updateBusInTrip(
  trip: ITripDetail | null,
  busId: string,
  changes: Partial<IBusDetail>
): ITripDetail | null {
  if (!trip) return trip;

  const updatedBuses = trip?.buses?.map((bus) => {
    if (bus._id !== busId) return bus;

    let newSeats: ISeatDetail[] = bus.seats;

    // 🔹 Nếu cập nhật seats, loại bỏ trùng lặp bằng seat._id
    if (changes?.seats) {
      const seen = new Map<string, ISeatDetail>();
      [...changes?.seats].forEach((seat) => {
        if (seat && seat?._id) seen.set(seat._id, seat);
      });
      newSeats = Array.from(seen.values());
    }

    return {
      ...bus,
      ...changes,
      ...(changes.seats ? { seats: newSeats } : {}),
    };
  });

  return {
    ...trip,
    buses: updatedBuses,
  };
}
